'use client';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useContext, } from 'react';
import { mergeProps, useButton, useFocusRing, useHover, useObjectRef, } from 'react-aria';
import { ButtonContext, useContextProps } from 'react-aria-components';
import { Icon } from '../system/icons';
import { ForceStateContext } from '../system/interactive';
import { MOTION, focusRing } from '../system/motion';
import { CONTROL_HEIGHT } from '../system/sizing';
import { TextTrim } from '../system/text-trim';
import { Spinner } from '../Spinner/Spinner';
const BTN_BASE = {
    letterSpacing: '0.005em',
    borderRadius: 'var(--radius-full)',
    border: '1px solid transparent',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 'var(--space-sm)',
    cursor: 'pointer',
    transition: MOTION.transition,
    whiteSpace: 'nowrap',
};
// Horizontal padding only — vertical centering is handled by the explicit
// `height` + `align-items: center` against the cap-trimmed label
// (see `TextTrim` in system/text-trim).
//
// `sm` uses label-sm (12/600) so the small button sits at the same text
// tier as Pill size="sm" — the two primitives are routinely paired in
// list rows (e.g. GroupCard's "Joined" Pill next to the next card's
// "Join" Button) and need to visually align.
// `md` uses label (14/600) — the default control tier.
// `lg` keeps the md text tier at the hero control height — it pairs with
// SearchInput size="lg" in landing-page filter rows.
const BTN_SIZES = {
    sm: { font: 'var(--type-label-sm)', padding: '0 var(--space-md)', height: CONTROL_HEIGHT.sm },
    md: { font: 'var(--type-label)', padding: '0 var(--space-lg)', height: CONTROL_HEIGHT.md },
    lg: { font: 'var(--type-label)', padding: '0 var(--space-xl)', height: CONTROL_HEIGHT.lg },
};
// Icon-only mode: button collapses to a square hit-target sized off the
// height tier. Icons scale up so the glyph reads at the larger button size.
const ICON_ONLY_GLYPH_SIZE = {
    sm: 16,
    md: 20,
    lg: 22,
};
const BTN_VARIANTS = {
    // Disabled = pale `--accent` fill + muted text, matching secondary /
    // ghost / soft. The old `--muted-foreground` background (dark gray-brown)
    // read as an active dark button because the lightness was nearly the
    // same as `--primary` — users couldn't tell disabled apart from enabled.
    primary: ({ hover, active, disabled }) => ({
        background: disabled ? 'var(--accent)' : active ? 'var(--primary-active)' : hover ? '#6e1113' : 'var(--primary)',
        color: disabled ? 'var(--muted-foreground)' : 'var(--background)',
    }),
    secondary: ({ hover, active, disabled }) => ({
        background: disabled ? 'var(--accent)' : active ? 'var(--accent)' : hover ? 'var(--accent)' : 'var(--card)',
        color: disabled ? 'var(--muted-foreground)' : 'var(--foreground)',
        border: `1px solid ${hover && !disabled ? 'var(--border-strong)' : 'var(--border)'}`,
    }),
    ghost: ({ hover, active, disabled }) => ({
        background: disabled ? 'transparent' : active ? 'var(--accent)' : hover ? 'var(--background)' : 'transparent',
        color: disabled ? 'var(--muted-foreground)' : 'var(--foreground)',
    }),
    ghostDanger: ({ hover, active, disabled }) => ({
        background: disabled ? 'transparent' : active ? 'rgba(178,31,36,.12)' : hover ? 'rgba(178,31,36,.06)' : 'transparent',
        color: disabled ? 'var(--muted-foreground)' : 'var(--destructive)',
    }),
    soft: ({ hover, active, disabled }) => ({
        background: disabled ? 'var(--accent)' : active ? 'var(--border)' : hover ? '#ebe2dd' : 'var(--accent)',
        color: disabled ? 'var(--muted-foreground)' : 'var(--foreground)',
    }),
    // Sibling of `primary` — same dark-fill shape, same disabled fix.
    solid: ({ hover, active, disabled }) => ({
        background: disabled ? 'var(--accent)' : active ? '#000' : hover ? '#2a2522' : 'var(--foreground)',
        color: disabled ? 'var(--muted-foreground)' : 'var(--background)',
    }),
    // Status indicator: "you've already done this." Flat across all states —
    // no hover/active shift — so consumers can render it next to a primary CTA
    // without it competing for attention. Pair with `disabled` to mark the
    // control non-interactive; the cursor stays `default` rather than the
    // global `not-allowed`, since "Joined" reads as completed, not forbidden.
    success: () => ({
        background: 'var(--light-green)',
        color: 'var(--palette-emerald)',
        border: '1px solid color-mix(in oklab, var(--success) 25%, transparent)',
    }),
    // Sibling of `success` — same flat status-indicator shape, in the
    // burgundy tone palette. Used for "you're going" / "you've committed
    // to this" states where green would read as a successful completion
    // rather than an active commitment.
    softDanger: () => ({
        background: 'var(--light-poppy)',
        color: 'var(--primary)',
        border: '1px solid color-mix(in oklab, var(--primary) 22%, transparent)',
    }),
    onDark: ({ hover, active, disabled }) => ({
        background: disabled ? 'rgba(255,255,255,.4)' : active ? 'rgba(255,255,255,.85)' : hover ? 'rgba(255,255,255,.95)' : 'var(--background)',
        color: 'var(--foreground)',
    }),
    onDarkGhost: ({ hover, active, disabled }) => ({
        background: active ? 'rgba(255,255,255,.16)' : hover ? 'rgba(255,255,255,.10)' : 'transparent',
        color: disabled ? 'rgba(255,255,255,.4)' : 'var(--background)',
        border: '1px solid rgba(255 255 255 / 12%)',
    }),
    onDarkSuccess: ({ hover, active, disabled }) => ({
        background: disabled
            ? 'rgba(46,160,67,.4)'
            : active
                ? 'var(--emerald-deep, #2a8b3a)'
                : hover
                    ? 'var(--emerald-hover, #2f9b41)'
                    : 'var(--emerald, #36a14a)',
        color: 'var(--background)',
    }),
    link: ({ hover, disabled }) => ({
        background: 'transparent',
        color: disabled ? 'var(--muted-foreground)' : 'var(--primary)',
        padding: 0,
        height: 'auto',
        textDecoration: hover && !disabled ? 'underline' : 'none',
        textUnderlineOffset: 3,
    }),
};
export const Button = forwardRef(function Button(props, forwardedRef) {
    const objectRef = useObjectRef(forwardedRef);
    // Pull trigger props (onPress, aria-haspopup, aria-expanded, …) out of
    // ButtonContext when this Button is the slot for a RAC trigger like
    // MenuTrigger or DialogTrigger. Without this the trigger silently no-ops.
    const [merged, ref] = useContextProps(props, objectRef, ButtonContext);
    const { variant = 'primary', size = 'md', icon, iconRight, block, disabled, loading, onClick, children, style, className = '', type = 'button', form, name, value, href, linkAs, target, rel, ...aria } = merged;
    const asAnchor = href !== undefined;
    const Anchor = linkAs ?? 'a';
    const isBusy = !!loading;
    const noClick = !!disabled || isBusy;
    // RAC triggers (MenuTrigger, DialogTrigger) supply `onPress` via
    // ButtonContext — that path already activates on keyboard and stays
    // authoritative. Everything else activates through the DOM `onClick`
    // (see handleClick's stopPropagation rationale), but react-aria
    // suppresses the browser's native Enter/Space → click synthesis, so
    // without a bridge keyboard activation is silently dropped: pointer
    // clicks work while Enter/Space on a focused Button (or a Radix
    // Popover trigger cloned onto it) does nothing. Re-dispatch a real
    // click so keyboard and pointer take the identical code path.
    const { onPress: contextOnPress, ...ariaSansPress } = aria;
    const { buttonProps, isPressed } = useButton({
        ...ariaSansPress,
        isDisabled: noClick,
        elementType: asAnchor ? 'a' : 'button',
        type,
        onPress: (e) => {
            if (contextOnPress) {
                contextOnPress(e);
                return;
            }
            if (e.pointerType === 'keyboard' && e.target instanceof HTMLElement) {
                e.target.click();
            }
        },
    }, ref);
    const { hoverProps, isHovered } = useHover({ isDisabled: noClick });
    const { focusProps, isFocusVisible } = useFocusRing();
    const forced = useContext(ForceStateContext);
    const state = {
        hover: !noClick && (forced?.hover ?? isHovered),
        active: !noClick && (forced?.active ?? isPressed),
        focus: !noClick && (forced?.focus ?? isFocusVisible),
        focusForced: !!forced?.focus && !noClick,
        // Loading buttons stay visually enabled — only true `disabled` greys out.
        disabled: !!disabled,
    };
    const variantStyle = BTN_VARIANTS[variant](state);
    // Icon-only mode: when there's an icon and no text/right-icon, render as a
    // square hit-target sized off the height tier so the button reads as a
    // glyph affordance rather than a stretched chip. A loading icon-only button
    // (spinner only) follows the same shape.
    const hasLeftSlot = icon !== undefined || isBusy;
    const iconOnly = hasLeftSlot && children === undefined && iconRight === undefined;
    const iconOnlyStyle = iconOnly
        ? { width: BTN_SIZES[size].height, padding: 0, justifyContent: 'center', gap: 0 }
        : undefined;
    const iconGlyphSize = iconOnly ? ICON_ONLY_GLYPH_SIZE[size] : 16;
    // Buttons are routinely rendered inside a clickable parent (Link, Card,
    // ListItem). Without stopPropagation the click runs the button's onClick
    // and *also* fires the parent's navigation — a bug shape we have hit
    // repeatedly. When the caller has no onClick the button is being used as
    // a styled child of an outer link (e.g. <Link><Button>Open</Button></Link>),
    // so we leave bubbling alone.
    const handleClick = onClick
        ? (e) => {
            e.stopPropagation();
            if (noClick)
                return;
            // onClick is typed for the button branch; in anchor mode the same
            // handler is rarely useful (consumers navigate via href) and the
            // event shape difference is structural only.
            onClick(e);
        }
        : undefined;
    const sharedStyle = {
        ...BTN_BASE,
        ...BTN_SIZES[size],
        ...variantStyle,
        // `success` is a status indicator ("Joined") — when paired with
        // `disabled` it should read as completed, not as a rejected control,
        // so its cursor stays `default` rather than the global `not-allowed`.
        cursor: isBusy ? 'wait' : disabled ? (variant === 'success' ? 'default' : 'not-allowed') : 'pointer',
        ...focusRing(state.focus),
        ...(block ? { width: '100%', justifyContent: 'center' } : null),
        ...iconOnlyStyle,
        // Anchors inherit text-decoration from the page — reset so the button
        // surface never shows the default underline. Belt-and-suspenders with
        // BTN_BASE letterSpacing.
        ...(asAnchor ? { textDecoration: 'none' } : null),
        ...style,
    };
    const innerChildren = (_jsxs(_Fragment, { children: [isBusy ? (_jsx(Spinner, { label: "", style: { width: iconGlyphSize, height: iconGlyphSize } })) : icon ? (_jsx(Icon, { size: iconGlyphSize, children: icon })) : null, children !== undefined && _jsx(TextTrim, { children: children }), iconRight && _jsx(Icon, { size: 16, children: iconRight })] }));
    // `slot: null` is meaningful for RAC ButtonContext opt-out but not assignable
    // to the DOM `slot` attribute (string | undefined). Coerce here so both
    // render paths can spread aria cleanly.
    const domSlot = aria.slot ?? undefined;
    const { slot: _slot, ...ariaRest } = aria;
    if (asAnchor) {
        // useButton's anchor mode emits role/tabIndex/keyboard handlers
        // appropriate for a clickable <a>. We strip the `type` prop (button-only)
        // and pass href/target/rel through. When `noClick` is true (disabled or
        // loading) we omit href entirely so the anchor is inert, matching the
        // <button disabled> semantics.
        const { type: _t, ...anchorAriaProps } = buttonProps;
        return (_jsx(Anchor, { ...mergeProps(anchorAriaProps, hoverProps, focusProps, ariaRest, { onClick: handleClick }), ref: ref, href: noClick ? undefined : href, target: target, rel: rel, slot: domSlot, "aria-busy": isBusy || undefined, "aria-disabled": noClick || undefined, className: className || undefined, style: sharedStyle, children: innerChildren }));
    }
    return (_jsx("button", { ...mergeProps(buttonProps, hoverProps, focusProps, ariaRest, { onClick: handleClick }), ref: ref, form: form, name: name, value: value, slot: domSlot, "aria-busy": isBusy || undefined, className: className || undefined, style: sharedStyle, children: innerChildren }));
});
//# sourceMappingURL=Button.js.map